import { useTranslation } from 'react-i18next';
import Status from '@ingka/status';
import {
  CHECKIN,
  REGISTERED,
  SELF_CHECKIN,
  WAITING_LIST,
} from '../../containers/Register/enums';
import {
  isUserRegisteredToEvent,
  useAppContext,
} from '../../contextProviders/AppContext';
import { CSSEvent, TimeSlot } from '../../types/model';
import {
  isTimeSlotFullyBooked,
  isRegistrationClosed,
  toTimestamp,
} from '../../utils';

const FULLY_BOOKED = 'FULLY_BOOKED';
const FEW_SPOTS_LEFT = 'FEW_SPOTS_LEFT';
const REGISTER_NOW = 'REGISTER_NOW';
const JOIN_WAITING_LIST = 'JOIN_WAITING_LIST';
const REGISTRATION_CLOSED = 'REGISTRATION_CLOSED';

type PropsType = {
  event: CSSEvent;
};

const statuses = {
  [FULLY_BOOKED]: {
    label: 'event.fullyBooked',
  },
  [FEW_SPOTS_LEFT]: {
    label: 'event.fewSpotsLeft',
  },
  [REGISTER_NOW]: {
    label: 'event.registerNow',
  },
  [JOIN_WAITING_LIST]: {
    label: 'register.joinOnWaitingList',
  },
  [REGISTRATION_CLOSED]: {
    label: 'event.registerClosed',
  },
};

function getCurrentCount(timeSlots: TimeSlot[] = []) {
  const now = Date.now();
  return timeSlots.reduce((acc, timeSlot: TimeSlot) => {
    if (toTimestamp(timeSlot.utcStartDate as number) > now) {
      return acc + timeSlot.currentRegistrationCount;
    }
    return acc;
  }, 0);
}

function getMaxCount(timeSlots: TimeSlot[] = []) {
  const now = Date.now();
  return timeSlots.reduce((acc, timeSlot: TimeSlot) => {
    if (toTimestamp(timeSlot.utcStartDate as number) > now) {
      let maxCount = timeSlot.registrationSettings.maxRegistrationCount;
      if (maxCount === 0) {
        maxCount = Number.MAX_SAFE_INTEGER;
      }
      return acc + maxCount;
    }
    return acc;
  }, 0);
}

const RegistrationStatus = ({ event }: PropsType) => {
  const { t } = useTranslation();
  const { useSelector } = useAppContext();
  const [isUserRegistered, registrationState] = useSelector(
    isUserRegisteredToEvent(event)
  );
  const LIMIT = 0.75;
  let status = statuses[REGISTER_NOW];
  const current = getCurrentCount(event.timeSlots);
  const max = getMaxCount(event.timeSlots);
  const registrationRequired =
    event?.registrationSettings?.registrationRequired;

  const isAnyFutureSlotFullyBooked = event.timeSlots.some(
    (timeSlot) =>
      toTimestamp(timeSlot.utcStartDate as number) > Date.now() &&
      isTimeSlotFullyBooked(timeSlot)
  );

  const isAnyFutureSlotNotFullyBooked = event.timeSlots.some(
    (timeSlot) =>
      toTimestamp(timeSlot.utcStartDate as number) > Date.now() &&
      !isTimeSlotFullyBooked(timeSlot)
  );

  const _isRegistrationClosed = event.timeSlots.every((x) =>
    isRegistrationClosed(toTimestamp(x.utcRegistrationClosedDate as number))
  );

  if (!registrationRequired) {
    return <span>{t('event.noRegistrationRequired')}</span>;
  }

  if (isUserRegistered) {
    const copy: { [k: string]: string } = {
      [REGISTERED]: 'event.registered',
      [CHECKIN]: 'event.registered',
      [SELF_CHECKIN]: 'event.registered',
      [WAITING_LIST]: 'event.onWaitingList',
    };

    return <Status label={t(copy[registrationState])} statusColor="green" />;
  }

  if (_isRegistrationClosed) {
    status = statuses[REGISTRATION_CLOSED];
  } else if (isAnyFutureSlotFullyBooked && !isAnyFutureSlotNotFullyBooked) {
    status = statuses[FULLY_BOOKED];
  } else if (current === max) {
    status = statuses[JOIN_WAITING_LIST];
  } else if (current / max > LIMIT) {
    status = statuses[FEW_SPOTS_LEFT];
  }

  return <p>{t(status.label)}</p>;
};

export default RegistrationStatus;